import { Capacitor } from "@capacitor/core";

/**
 * Native platform detection for the Cohesive mobile app.
 *
 * The mobile UI lives inside user-dashboard and is switched on by these checks;
 * the native shell is packages/user-mobile (Capacitor 8).
 *
 * Two levels of "native":
 *
 * - {@link isNativePlatform} — true in the real iOS container, when
 *   `VITE_FORCE_NATIVE=true`, or via the dev-only browser switch (visit
 *   `?forceNative=1` to turn it on, `?forceNative=0` to turn it off). Use this for
 *   **UI gates** (mobile home/routes, forcing the responsive layout, hiding
 *   signup, safe-area padding). This lets us exercise the whole mobile UI
 *   in a desktop browser at phone width without a device.
 *
 * - {@link isRealNativePlatform} — true **only** inside the real Capacitor
 *   container. Use this for behaviors that would break a normal browser
 *   (the custom-scheme Auth0 redirect, opening OAuth in the system browser,
 *   the `appUrlOpen` deep-link listener, StatusBar, the external-link shim)
 *   AND for the Apple 3.1.1 billing exclusions — a browser must always keep
 *   billing/plan/checkout, even with the forceNative switch on. Under
 *   `VITE_FORCE_NATIVE` the auth behaviors stay off so browser login still
 *   works against the normal SPA Auth0 client.
 */

const FORCE_NATIVE_KEY = "cohesive-force-native";

/**
 * Dev-only browser switch so you can drive the native UI without a device:
 * `?forceNative=1` turns it on (persisted in localStorage), `?forceNative=0`
 * turns it off. Only active under `vite dev` and only affects
 * {@link isNativePlatform} — auth stays the normal SPA flow.
 */
function readForceNativeOverride(): boolean {
  if (!import.meta.env.DEV || typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search).get("forceNative");
    if (q === "1" || q === "true") {
      localStorage.setItem(FORCE_NATIVE_KEY, "true");
      return true;
    }
    if (q === "0" || q === "false") {
      localStorage.removeItem(FORCE_NATIVE_KEY);
      return false;
    }
    return localStorage.getItem(FORCE_NATIVE_KEY) === "true";
  } catch {
    return false;
  }
}

const forceNative =
  import.meta.env.VITE_FORCE_NATIVE === "true" || readForceNativeOverride();

export function isNativePlatform(): boolean {
  return forceNative || Capacitor.isNativePlatform();
}

export function isRealNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/** The Capacitor platform string: "ios" | "android" | "web". */
export function getPlatform(): string {
  return Capacitor.getPlatform();
}
